import {
	MouseEvent,
	PropsWithChildren,
	ReactElement,
	useContext
} from "react";
import {
	generateTrackingId,
	isNotNil,
	setTrackingId
} from "client/Misc/utils";
import styles from "./ReorderableList.module.less";
import { ReorderableListItemBase } from "./ReorderableListItemBase";
import { ReorderableListDragIndicator } from "./ReorderableListDragIndicator";
import { ReorderableListDragHandle } from "./ReorderableListDragHandle";
import classNames from "classnames";
import { ReorderableListItemContext } from "./ReorderableListItemContext";
import { ValidationError } from "../ValidationError/ValidationError";
import { useDraggable, useIsTouchDevice } from "client/Hooks";
import { ReorderableListItemMouseInteractionConfiguration } from "./ReorderableListRowItem";
import { Coordinate } from "client/Misc/commonTypes";

export type ReorderableListGroupDraggableMode = "header" | "handle";

export type ReorderableListGroupProps = {
	headerContent: ReactElement | string;
	actions?: ReactElement;
	dontEllipseText?: boolean;
	isContentInteractable?: boolean;
	validationMessage?: string;
	trackingId: string;
	noSpaceAroundHeaderContent?: boolean;
	isActive?: boolean;
	mouseInteraction?: ReorderableListItemMouseInteractionConfiguration<ReorderableListGroupDraggableMode>;
};

export const ReorderableListGroup = (props: PropsWithChildren<ReorderableListGroupProps>): ReactElement => {
	const {
		dontEllipseText,
		isContentInteractable = false,
		headerContent,
		actions,
		trackingId,
		validationMessage,
		children,
		noSpaceAroundHeaderContent = false,
		isActive = false,
		mouseInteraction
	} = props;

	const mouseDraggableMode: ReorderableListGroupDraggableMode = mouseInteraction?.draggableMode
		?? "header";

	const onMouseContextMenu: ((coordinate: Coordinate) => void) | undefined = mouseInteraction
		?.onContextMenu;

	const {
		isDragging,
		isReorderEnabled,
		dragMove,
		dragStart,
		dragStop
	} = useContext(ReorderableListItemContext);

	const isTouchDevice: boolean = useIsTouchDevice();

	const {
		ref: draggableRef
	} = useDraggable({
		onStart: dragStart,
		onMove: dragMove,
		onStop: dragStop,
		isEnabled: isReorderEnabled
	});

	const isHoverable: boolean = isContentInteractable
		|| isReorderEnabled
		|| (!isTouchDevice && isNotNil(onMouseContextMenu));

	const contextMenuHandler: ((args: MouseEvent) => void) | undefined = !isTouchDevice
		&& isNotNil(onMouseContextMenu)
			? (args: MouseEvent): void => {
				args.preventDefault();

				onMouseContextMenu({
					x: args.clientX,
					y: args.clientY
				});
			}
			: undefined;

	return (
		<ReorderableListItemBase
			trackingId={trackingId}
		>
			<ReorderableListDragIndicator>
				<div
					{...setTrackingId("content")}
					ref={
						!isTouchDevice && mouseDraggableMode === "header"
							? draggableRef
							: undefined
					}
					className={
						classNames(
							styles.rowItem,
							{
								[styles.rowItem_hoverable]: isHoverable,
								[styles.rowItem_active]: isActive
							}
						)
					}
					onContextMenu={contextMenuHandler}
				>
					<ReorderableListDragHandle
						isReorderEnabled={isReorderEnabled}
						ref={
							isTouchDevice || mouseDraggableMode === "handle"
								? draggableRef
								: undefined
						}
					/>
					<div className={classNames(
						styles.rowItemContent,
						{
							[styles.rowItemContent_ellipsed]: !dontEllipseText,
							[styles.rowItemContent_noSpaceAround]: noSpaceAroundHeaderContent
						}
					)}>
						{headerContent}
					</div>
					<div className={styles.rowItemActions}>
						{actions}
					</div>
				</div>
			</ReorderableListDragIndicator>
			<ValidationError
				message={validationMessage}
				trackingId={generateTrackingId(trackingId, "__validation-error")}
			/>
			<div
				{...setTrackingId("children")}
				className={classNames(
					styles.groupChildren,
					{ [styles.groupChildren_hidden]: isDragging }
				)}
			>
				{children}
			</div>
		</ReorderableListItemBase>
	);
};
ReorderableListGroup.displayName = "ReorderableListGroup";